import { useCallback, useEffect, useState } from 'react'
import { useQuery } from 'react-query'

import { useCurrentSite } from '../components/SitesProvider'
import request from '../config/axios'

export type QRSessionStatus =
  | 'pending'
  | 'scanned'
  | 'loaded'
  | 'expired'
  | 'error'

interface QRSessionTrackingResult {
  status: QRSessionStatus
  isLoading: boolean
  error: string | null
  startTracking: () => void
  stopTracking: () => void
}

/**
 * Hook to track QR code scan sessions
 * Polls backend to detect when QR URL has been accessed
 */
const useQRSessionTracking = (sessionId: string): QRSessionTrackingResult => {
  const trustedSiteId = useCurrentSite()?.id
  const [isTracking, setIsTracking] = useState(false)
  const [status, setStatus] = useState<QRSessionStatus>('pending')
  const [error, setError] = useState<string | null>(null)

  // Query to check QR session status
  const { data, isLoading, error: queryError } = useQuery(
    ['qr-session-status', sessionId],
    async () => {
      if (!sessionId || !isTracking) return null

      try {
        const response = await request.get(
          `/impacts/qr_sessions/${sessionId}/status`,
          {
            params: {
              site_ids: [trustedSiteId],
            },
          },
        )
        return response.data
      } catch (err) {
        // Fallback: Backend endpoint not ready yet
        return { status: 'pending', accessed_at: null }
      }
    },
    {
      enabled: Boolean(sessionId && isTracking), // Only poll when tracking is active
      refetchInterval: 5000, // Poll every 5 seconds
      retry: 3,
      retryDelay: 1000,
      onError: (_err) => {
        setError('Failed to track QR session')
      },
    },
  )

  // Update status based on query result
  useEffect(() => {
    if (data) {
      setStatus(data.status || 'pending')
      setError(null)
    }
  }, [data])

  // Handle query errors
  useEffect(() => {
    if (queryError) {
      setStatus('error')
      setError('Unable to track QR session')
    }
  }, [queryError])

  // Timeout logic - if tracking for too long, mark as expired
  useEffect(() => {
    if (!isTracking) return

    const timeout = setTimeout(() => {
      if (status === 'pending') {
        setStatus('expired')
        setIsTracking(false)
      }
    }, 60000) // 60 second timeout

    return () => clearTimeout(timeout)
  }, [isTracking, status])

  const startTracking = useCallback(() => {
    setIsTracking(true)
    setStatus('pending')
    setError(null)
  }, [])

  const stopTracking = useCallback(() => {
    setIsTracking(false)
  }, [])

  // Stop tracking when component unmounts
  useEffect(() => {
    return () => {
      setIsTracking(false)
    }
  }, [])

  return {
    status,
    isLoading: isLoading && isTracking,
    error,
    startTracking,
    stopTracking,
  }
}

export default useQRSessionTracking
